import React, { useState, useEffect } from 'react';
import { Lock, Check, Eye, EyeOff } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { authApi } from '../../api/client';
import './DashForm.css';
import '../../pages/AuthPage.css';

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

function PasswordChecks({ password }) {
  const { t } = useLanguage();
  const checks = [
    { key: 'passwordReqMin', ok: password.length >= 8 },
    { key: 'passwordReqUpper', ok: /[A-Z]/.test(password) },
    { key: 'passwordReqLower', ok: /[a-z]/.test(password) },
    { key: 'passwordReqNumber', ok: /[0-9]/.test(password) },
    { key: 'passwordReqSpecial', ok: /[^A-Za-z0-9]/.test(password) },
  ];

  return (
    <ul
      className="dform-checklist"
      style={{
        margin: '6px 0 0',
        padding: 0,
        listStyle: 'none',
        fontSize: '0.78rem',
        display: 'grid',
        gap: 4,
      }}
    >
      {checks.map((c) => (
        <li
          key={c.key}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            color: c.ok ? '#10b981' : 'var(--dash-muted)',
          }}
        >
          <span style={{ fontWeight: 700, width: 14 }}>{c.ok ? '✓' : '○'}</span>
          {t.auth[c.key]}
        </li>
      ))}
    </ul>
  );
}

function PwdField({ label, value, onChange, autoComplete, show, onToggle, minLength, hint }) {
  const { t } = useLanguage();
  return (
    <div className="dform-field">
      <label>
        {label} <span className="dform-req">*</span>
      </label>
      <div className="dform-pwd">
        <input
          type={show ? 'text' : 'password'}
          required
          minLength={minLength}
          autoComplete={autoComplete}
          value={value}
          onChange={onChange}
        />
        <button
          type="button"
          className="dform-pwd-toggle"
          onClick={onToggle}
          aria-label={show ? t.auth.hidePassword : t.auth.showPassword}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      {hint && <div style={{ marginTop: 6 }}>{hint}</div>}
    </div>
  );
}

export default function PasswordChangeForm() {
  const { t } = useLanguage();
  const [current, setCurrent] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState({ current: false, new: false, confirm: false });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);

  const reset = () => {
    setCurrent('');
    setNewPass('');
    setConfirm('');
  };

  const toggle = (key) => setShow((prev) => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    if (!touched) return;
    if (!current || !newPass || !confirm) {
      setError(t.auth.invalidPayload || 'Veuillez remplir tous les champs obligatoires.');
    } else {
      setError('');
    }
  }, [touched, current, newPass, confirm, t, setError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!current || !newPass || !confirm) {
      setError(t.auth.invalidPayload);
      return;
    }
    if (newPass !== confirm) {
      setError(t.auth.passwordsMismatch);
      return;
    }
    if (!PASSWORD_REGEX.test(newPass)) {
      setError(t.auth.passwordHint);
      return;
    }

    setBusy(true);
    try {
      await authApi.changePassword({ currentPassword: current, newPassword: newPass });
      setSuccess(true);
      reset();
    } catch (err) {
      setError(t.auth[err.data?.error] || err.data?.error || t.auth.invalidCredentials);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="dform" onSubmit={handleSubmit} noValidate>
      <section className="dform-section dform-section--ghost">
        <header className="dform-section-head">
          <Lock size={17} />
          <h4>{t.auth.changePassword || 'Changer le mot de passe'}</h4>
        </header>
        <div className="dform-grid dform-grid--2">
          <div className="dform-wide">
            <PwdField
              label={t.auth.currentPassword || 'Mot de passe actuel'}
              value={current}
              onChange={(e) => { setCurrent(e.target.value); setTouched(true); }}
              autoComplete="current-password"
              show={show.current}
              onToggle={() => toggle('current')}
            />
          </div>
          <PwdField
            label={t.auth.newPassword || 'Nouveau mot de passe'}
            value={newPass}
            onChange={(e) => { setNewPass(e.target.value); setTouched(true); }}
            autoComplete="new-password"
            show={show.new}
            onToggle={() => toggle('new')}
            minLength={8}
            hint={<PasswordChecks password={newPass} />}
          />
          <PwdField
            label={t.auth.confirmNewPassword || 'Confirmer le nouveau mot de passe'}
            value={confirm}
            onChange={(e) => { setConfirm(e.target.value); setTouched(true); }}
            autoComplete="new-password"
            show={show.confirm}
            onToggle={() => toggle('confirm')}
          />
        </div>
        {error && (
          <p className="auth-error" role="alert">{error}</p>
        )}
        {success && (
          <p style={{ color: '#059669', marginTop: 12, fontSize: '0.85rem' }}>{t.auth.passwordChanged}</p>
        )}
        <footer className="dform-footer" style={{ marginTop: 16 }}>
          <span className="dform-spacer" />
          <button type="submit" className="btn btn-primary" disabled={busy}>
            <Check size={15} /> {t.auth.changePassword || 'Changer'}
          </button>
        </footer>
      </section>
    </form>
  );
}
